/**
 * Helpers pour la gestion des adresses
 * Fonctions utilitaires pour formater, valider et comparer des adresses
 */

// ===== REGEX CONSTANTS =====
// Expressions régulières réutilisables pour le traitement et la normalisation des adresses

/**
 * Détecte les codes postaux canadiens (formats: A1A 1A1 ou A1A1A1)
 */
export const POSTAL_CODE_REGEX = /[A-Z]\d[A-Z]\s?\d[A-Z]\d/gi;

/**
 * Détecte les boîtes postales en français et anglais
 * Patterns: P.O. Box, PO Box, CP, Case Postale, Boîte Postale, BP
 */
export const PO_BOX_REGEX =
  /\b(p\.?\s?o\.?\s?box|po\s?box|c\.?p\.?\s?\d+|case\s?postale|bo[iî]te\s?postale|b\.?p\.?\s?\d+)\b/i;

/**
 * Detecte les resultats AddressFind qui representent une case postale.
 * Plus permissif que PO_BOX_REGEX pour couvrir les containers "CP" sans numero.
 */
export const PO_BOX_RESULT_REGEX =
  /^\s*(p\.?\s?o\.?\s?box|po\s?box|c\.?\s*p\.?|case\s?postale|casier\s?postal|bo[i\u00ee]te\s?postale|b\.?\s*p\.?)\b/i;

/**
 * Détecte les types de rues et points cardinaux pour normalisation
 * Types: rue, avenue, boulevard, chemin, etc.
 * Points cardinaux: nord, sud, est, ouest
 */
export const STREET_TYPES_CARDINALS_REGEX =
  /\b(rue|r\.|avenue|av\.|boulevard|boul\.|bd|blvd\.|chemin|ch\.|route|rte\.|place|pl\.|montee|montée|rang|cote|côte|allee|allée|carre|carré|croissant|crois\.|impasse|imp\.|parc|passage|pass\.|promenade|prom\.|terrasse|terr\.|voie|nord|n\.|n|sud|s\.|s|est|e\.|e|ouest|o\.|o)\b/gi;

/**
 * Détecte les préfixes d'appartement (#, App, Apt)
 */
export const APARTMENT_PREFIX_REGEX = /\b(#|app|apt)\s+(\d+)/gi;

/**
 * Détecte les provinces canadiennes autres que le Québec
 */
export const OTHER_CANADIAN_PROVINCES_REGEX =
  /\b(?:ON|BC|AB|MB|SK|NS|NB|NL|PE|YT|NT|NU)\b/i;

/**
 * Patterns pour détecter les références aux cases postales
 * Utilisé pour validation et nettoyage d'adresses
 */
export const POSTAL_BOX_PATTERNS = [
  /\b(c\.?p\.?|b\.?p\.?|p\.?o\.?)\b/gi, // Abréviations: C.P, B.P, P.O
  /\b(case postale|boîte postale|postal box|post office box|box|bin)\b/gi, // Formes complètes
];

/**
 * Échappe les caractères spéciaux pour utilisation dans une regex
 * @param {string} str - Chaîne à échapper
 * @returns {string} - Chaîne échappée
 */
export const escapeRegex = (str) => str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

// ===== HELPER FUNCTIONS =====

/**
 * Compare deux adresses et retourne true si elles sont identiques
 * @param {Object} address1 - Première adresse
 * @param {Object} address2 - Deuxième adresse
 * @returns {boolean} - True si identiques
 */
export function compareAddresses(address1, address2) {
  if (!address1 || !address2) return false;

  const fields = ['numero', 'rue', 'ville', 'appartement', 'complementAdresse', 'codePostal'];

  return fields.every((field) => {
    const value1 = normalizeAddress(address1[field] || '');
    const value2 = normalizeAddress(address2[field] || '');
    return value1 === value2;
  });
}

/**
 * Normalise une chaîne d'adresse pour la comparaison
 * Effectue trim, minuscule, normalisation des espaces et apostrophes
 * @param {string} value - Valeur à normaliser
 * @returns {string} - Valeur normalisée
 */
export function normalizeAddress(value) {
  return value
    .toString()
    .trim()
    .toLowerCase()
    .replace(/\s+/g, ' ') // Remplace espaces multiples par un seul
    .replace(/['']/g, "'"); // Normalise les apostrophes
}

// ===== FORMATTING UTILITIES =====

/**
 * Construit une ligne d'adresse à partir du numéro et de la rue
 * @param {string} numero - Numéro civique
 * @param {string} rue - Nom de rue
 * @returns {string} - Ligne d'adresse formatée
 */
export function buildAddressLine(numero, rue) {
  if (!numero && !rue) return '';
  if (!numero) return rue;
  if (!rue) return numero;
  return `${numero}, ${rue}`;
}

/**
 * Nettoie une rue en retirant les préfixes d'appartement APP/APT
 * Utilisé pour normaliser les adresses retournées par StreetPerfect
 * @param {string} rue - La rue à nettoyer
 * @returns {string} La rue nettoyée sans préfixes d'appartement
 */
export function cleanApartmentPrefix(rue) {
  if (!rue) return '';

  // Retire les préfixes APP ou APT et tout ce qui suit
  return rue.replace(/\s+(APP|APT)\s+.*$/i, '').trim();
}

/**
 * Nettoie les références aux cases postales d'une adresse
 * @param {string} value - Texte à nettoyer
 * @returns {string} - Texte nettoyé
 */
export function cleanPostalBoxReferences(value) {
  if (!value) return '';

  let cleaned = value;
  POSTAL_BOX_PATTERNS.forEach((pattern) => {
    cleaned = cleaned.replace(pattern, '');
  });

  // Nettoyer espaces multiples résultants
  return cleaned.replace(/\s+/g, ' ').trim();
}

/**
 * Normalise une adresse au format standard de l'application
 * Gère les variations de noms de champs entre différentes réponses d'adresse
 * @param {Object} address - Adresse retournée (Verify ou autre)
 * @returns {Object} Adresse normalisée au format standard
 */
export function formatAddress(address) {
  if (!address) return null;

  return {
    numero: address.noCivique || address.numero || '',
    rue: address.rue || '',
    appartement: address.noAppartement || address.appartement || '',
    complementAdresse: address.complementAdresse || '',
    ville: address.ville || '',
    codePostal: address.codePostal || '',
    province: address.province || 'QC',
  };
}

/**
 * Formatte les détails de l'adresse retournée en objet AddressFormData
 * Compatible avec la structure attendue par le store Vuex
 *
 * @param {Object} addressDetails - Détails retournés par retrieve()
 * @returns {Object} Adresse formatée pour le store
 */
export function formatAddressDetailsToAddressFormData(addressDetails) {
  if (!addressDetails) return null;

  return {
    numero: addressDetails.BuildingNumber || '',
    rue: addressDetails.Street || '',
    ligneAdresse:
      addressDetails.BuildingNumber && addressDetails.Street
        ? `${addressDetails.BuildingNumber}, ${addressDetails.Street}`
        : '',
    appartement: addressDetails.SubBuilding || '',
    complementAdresse: addressDetails.SecondaryStreet || addressDetails.DeliveryAddress2 || '',
    ville: addressDetails.City || '',
    codePostal: addressDetails.PostalCode || '',
    province: addressDetails.Province || 'QC',
  };
}

/**
 * Formatte une adresse pour l'affichage
 * @param {Object} address - Adresse à formatter
 * @param {boolean} includeProvince - Inclure la province
 * @returns {string} - Adresse formatée
 */
export function formatAddressForDisplay(address, includeProvince = true) {
  if (!address) return '';

  const parts = [];

  // Ligne 1: Numéro + Rue + Appartement + Complément d'adresse
  const line1Parts = [];
  if (address.numero) line1Parts.push(address.numero);
  if (address.rue) line1Parts.push(address.rue);
  if (address.appartement) line1Parts.push(`App. ${address.appartement}`);
  if (address.complementAdresse) line1Parts.push(address.complementAdresse);

  if (line1Parts.length > 0) {
    parts.push(line1Parts.join(', '));
  }

  // Ligne 2: Ville + Province + Code Postal
  const line2Parts = [];
  if (address.ville) line2Parts.push(address.ville);
  if (includeProvince && address.province) line2Parts.push(`(${address.province})`);
  if (address.codePostal) line2Parts.push(address.codePostal);

  if (line2Parts.length > 0) {
    parts.push(line2Parts.join(' '));
  }

  return parts.join('\n');
}

/**
 * Formate une adresse avec appartement pour l'affichage
 * Retourne une adresse formatée sur deux lignes avec saut de ligne (\n)
 * @param {Object} address - L'adresse à formater
 * @param {string} locale - La locale ('en' ou 'fr') pour le préfixe appartement (défaut: 'fr')
 * @returns {string} L'adresse formatée avec saut de ligne \n
 * @example
 * formatAddressWithApartment({numero: '100', rue: 'Main St', appartement: '5', ville: 'Montreal', codePostal: 'H1A1A1'}, 'fr')
 * // Returns: "100, Main St, App 5\nMontreal, H1A 1A1"
 */
export function formatAddressWithApartment(address, locale = 'fr') {
  if (!address) return '';

  const streetParts = [];
  const cityParts = [];

  // Ligne 1: Adresse principale (numéro + rue ou ligne d'adresse)
  if (address.numero && address.rue) {
    streetParts.push(`${address.numero}, ${address.rue}`);
  } else if (address.ligneAdresse) {
    streetParts.push(address.ligneAdresse);
  }

  // Ajout de l'appartement avec le préfixe approprié selon la locale
  if (address.appartement) {
    streetParts.push(`${locale === 'en' ? 'Apt' : 'App'} ${address.appartement}`);
  }

  // Ajout du complément d'adresse si présent (ex: "RR 1" pour Route Rurale)
  if (address.complementAdresse) {
    streetParts.push(address.complementAdresse);
  }

  // Ligne 2: Ville, province et code postal
  if (address.ville) cityParts.push(address.ville);
  if (address.province) cityParts.push(address.province);
  if (address.codePostal) cityParts.push(formatteCodePostal(address.codePostal));

  return `${streetParts.join(', ')}\n${cityParts.join(', ')}`;
}

/**
 * Formate un code postal canadien avec un espace après les 3 premiers caractères
 * Fonction déplacée de StringHelpers car elle est spécifique aux adresses
 * @param {string} postcode - Code postal à formater (ex: "H1A1A1" ou "H1A 1A1")
 * @returns {string} Code postal formaté (ex: "H1A 1A1")
 * @example
 * formatteCodePostal('H1A1A1') // Returns: "H1A 1A1"
 * formatteCodePostal('H1A 1A1') // Returns: "H1A 1A1"
 */
export function formatteCodePostal(postcode) {
  if (!postcode) return '';
  const cleaned = postcode.toString().toUpperCase().replace(/\W/g, '');
  return cleaned.replace(/(...)/, '$1 ');
}

/**
 * Parse une ligne d'adresse en numéro et rue
 * Gère les formats: "100 Main Street", "100, Main Street", "100-Main Street"
 * @param {string} addressLine - Ligne d'adresse complète
 * @returns {Object} - { numero, rue, separator }
 */
export function parseAddressLine(addressLine) {
  if (!addressLine) {
    return { numero: '', rue: '', separator: '' };
  }

  const trimmed = addressLine.trim();

  // Détecter le séparateur (virgule, espace, tiret)
  let separator = '';
  if (trimmed.includes(',')) {
    separator = ',';
  } else if (trimmed.includes(' ')) {
    separator = ' ';
  }

  if (!separator) {
    return { numero: '', rue: '', separator: '', ligneAdresse: trimmed };
  }

  // Séparer numéro et rue
  const parts = trimmed.split(separator);
  const numero = parts[0].trim();
  const rue = parts.slice(1).join(separator).trim();

  // Reformatter avec virgule + espace
  const ligneAdresse = numero && rue ? `${numero}, ${rue}` : trimmed;

  return {
    numero,
    rue,
    separator,
    ligneAdresse,
  };
}

/**
 * Convertit une adresse du format store vers le format API
 * @param {Object} storeAddress - Adresse depuis le store Vuex
 * @returns {Object} - Adresse au format API
 */
export function storeAddressToApiFormat(storeAddress) {
  return {
    addressLine1: storeAddress.numero || '',
    addressLine2: storeAddress.rue || '',
    addressLine3: storeAddress.appartement || '',
    city: storeAddress.ville || '',
    postcode: storeAddress.codePostal || '',
    countryCode: 'CAN',
    state: 'QC',
  };
}

/**
 * Convertit une adresse du format API vers le format store
 * @param {Object} apiAddress - Adresse depuis l'API
 * @returns {Object} - Adresse au format store
 * @note addressLine2 peut contenir rue + complementAdresse séparés par un espace
 */
export function apiAddressToStoreFormat(apiAddress) {
  // Séparer rue et complément d'adresse si addressLine2 contient les deux
  // Le complément d'adresse est ajouté après la rue avec un espace
  const addressLine2 = apiAddress.addressLine2 || '';

  // Pattern pour détecter un complément d'adresse à la fin (ex: "RR 1", "Route 123")
  const complementMatch = addressLine2.match(/\s+(RR\s+\d+|Route\s+\d+|CP\s+\d+)$/i);

  let rue = addressLine2;
  let complementAdresse = '';

  if (complementMatch) {
    complementAdresse = complementMatch[1].trim();
    rue = addressLine2.substring(0, complementMatch.index).trim();
  }

  return {
    numero: apiAddress.addressLine1 || '',
    rue,
    appartement: apiAddress.addressLine3 || '',
    complementAdresse,
    ville: apiAddress.city || '',
    codePostal: apiAddress.postcode || '',
    province: apiAddress.state || 'QC',
  };
}

// ===== VALIDATE UTILITIES =====

/**
 * Valide si une adresse est complète
 * @param {Object} address - Adresse à valider
 * @returns {Object} - { isValid, missingFields }
 */
export function validateAddressCompleteness(address) {
  const requiredFields = ['numero', 'rue', 'ville', 'codePostal'];
  const missingFields = requiredFields.filter(
    (field) => !address[field] || address[field].trim() === ''
  );

  return {
    isValid: missingFields.length === 0,
    missingFields,
  };
}

/**
 * Détecte les différences entre deux adresses
 * @param {Object} address1 - Première adresse
 * @param {Object} address2 - Deuxième adresse
 * @returns {Object} - Map des champs différents avec leurs valeurs
 */
export function detectAddressDifferences(address1, address2) {
  const fields = ['numero', 'rue', 'ville', 'appartement', 'complementAdresse', 'codePostal'];
  const differences = {};

  fields.forEach((field) => {
    const value1 = normalizeAddress(address1[field] || '');
    const value2 = normalizeAddress(address2[field] || '');

    if (value1 !== value2) {
      differences[field] = {
        original: address1[field] || '',
        suggested: address2[field] || '',
      };
    }
  });

  return differences;
}

/**
 * Vérifie si une valeur contient une référence à une case postale
 * @param {string} value - Valeur à vérifier
 * @returns {boolean} - True si contient une case postale
 */
export function hasPostalBoxReference(value) {
  if (!value) return false;

  return POSTAL_BOX_PATTERNS.some((pattern) => {
    pattern.lastIndex = 0;
    return pattern.test(value);
  });
}

export function isPostalBoxAddressResult(result) {
  const resultText = `${result?.Text || ''} ${result?.Description || ''}`.trim();

  if (!resultText) {
    return false;
  }

  return PO_BOX_RESULT_REGEX.test(resultText) || PO_BOX_REGEX.test(resultText);
}

/**
 * Vérifie qu'un résultat AddressFind correspond à la province du Québec (QC)
 * et non à une autre province canadienne.
 *
 * Les résultats AddressFind n'affichent pas toujours explicitement "QC"
 * (ex: containers partiels durant la saisie), donc l'absence de mention de
 * province n'exclut pas le résultat: seule la présence explicite d'une autre
 * province canadienne (ON, BC, AB, etc.) l'exclut.
 *
 * @param {Object} result - Résultat de la recherche (Text/Description)
 * @returns {boolean} - True si aucune autre province canadienne n'est détectée
 */
export function isQuebecAddressResult(result) {
  const resultText = `${result?.Text || ''} ${result?.Description || ''}`.trim();

  if (!resultText) {
    return true;
  }

  if (OTHER_CANADIAN_PROVINCES_REGEX.test(resultText)) {
    return false;
  }

  return true;
}

/**
 * Vérifie si une adresse doit être soumise directement sans confirmation
 * (Adresse identique à la suggestion)
 * @param {Object} enteredAddress - Adresse saisie
 * @param {Object} suggestedAddress - Adresse suggérée
 * @returns {boolean} - True si soumission directe possible
 */
export function canSubmitDirectly(enteredAddress, suggestedAddress) {
  if (!suggestedAddress) return false;
  return compareAddresses(enteredAddress, suggestedAddress);
}

/**
 * Génère un résumé des changements pour l'affichage
 * @param {Object} differences - Différences détectées
 * @returns {string} - Résumé formaté
 */
export function generateDifferencesSummary(differences) {
  const labels = {
    numero: 'Numéro civique',
    rue: 'Rue',
    ville: 'Ville',
    appartement: 'Appartement',
    complementAdresse: "Complément d'adresse",
    codePostal: 'Code postal',
  };

  const changes = Object.entries(differences).map(([field, values]) => {
    return `${labels[field]}: "${values.original}" → "${values.suggested}"`;
  });

  return changes.join('\n');
}

export default {
  // Regex constants
  POSTAL_CODE_REGEX,
  PO_BOX_REGEX,
  PO_BOX_RESULT_REGEX,
  POSTAL_BOX_PATTERNS,
  STREET_TYPES_CARDINALS_REGEX,
  APARTMENT_PREFIX_REGEX,
  OTHER_CANADIAN_PROVINCES_REGEX,
  escapeRegex,
  // Helper functions
  apiAddressToStoreFormat,
  buildAddressLine,
  canSubmitDirectly,
  cleanApartmentPrefix,
  cleanPostalBoxReferences,
  compareAddresses,
  detectAddressDifferences,
  formatAddressDetailsToAddressFormData,
  formatAddressForDisplay,
  formatAddressWithApartment,
  formatteCodePostal,
  generateDifferencesSummary,
  hasPostalBoxReference,
  isPostalBoxAddressResult,
  isQuebecAddressResult,
  normalizeAddress,
  formatAddress,
  parseAddressLine,
  storeAddressToApiFormat,
  validateAddressCompleteness,
};
